import { useEffect, useSyncExternalStore } from 'react';
import { useIsMutating } from 'react-query';
import { clearUnresolvedPlan, unresolvedPlans, subscribeUnresolvedPlans, type PlanAttempt } from '../../../lib/reminderPlanGuard';
import { planWriteKey } from './useReminderPlanActions';
import { useScheduledBroadcasts } from './useScheduledBroadcasts';

export interface PlanCreateState {
  /** A create of this tab whose outcome is unknown: the SAME plan is retried (never a new id), or discarded. */
  unresolved: PlanAttempt | null;
  /** A plan write for this trip is running right now, possibly from an earlier mount of the dialog. */
  writing: boolean;
  /** Creating a new plan right now would risk a second plan for one intent. */
  blocked: boolean;
}

/**
 * May a NEW plan be created for this trip? Yes, always — several plans may coexist and a plan is created atomically,
 * so nothing about OTHER plans (however they look) can block it. Exactly two things can:
 *  - a plan write is running (a create from an earlier mount of the dialog), and
 *  - an earlier create of this tab has an unknown outcome and has not resolved: a new id for the same intent could
 *    make a second plan if that one landed. It resolves the moment its rows appear on the scheduled list (the call is
 *    atomic, so ANY row means the whole plan exists), or when the host retries it (same id) or discards it.
 * So there is no state in which Create is disabled and the host has no way forward.
 */
export function usePlanCreateBlock(groupId: string): PlanCreateState {
  const { rows } = useScheduledBroadcasts(groupId);
  const attempts = useSyncExternalStore(
    subscribeUnresolvedPlans,
    () => unresolvedPlans(groupId),
    () => unresolvedPlans(''),
  );
  const writing = useIsMutating(planWriteKey(groupId)) > 0;

  // A create that landed is on the list: resolved.
  const landed = attempts.filter((attempt) => rows.some((row) => row.sequenceId === attempt.sequenceId));
  useEffect(() => {
    landed.forEach((attempt) => clearUnresolvedPlan(groupId, attempt.sequenceId));
  }, [landed.map((attempt) => attempt.sequenceId).join(','), groupId]); // eslint-disable-line react-hooks/exhaustive-deps

  const unresolved = writing ? null : attempts.find((attempt) => !landed.includes(attempt)) ?? null;
  return { unresolved, writing, blocked: writing || unresolved !== null };
}
